import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
  gql,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import {
  ApolloProvider,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import { FunctionComponent, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

// import { persistCache, LocalStorageWrapper } from 'apollo3-cache-persist';
import { jwtDecode } from 'jwt-decode';
import { setContext } from '@apollo/client/link/context';
import { useAppContext } from '@providers/app/app.context';
import { useAuth } from '@providers/auth';
import { useSettings } from '@providers/settings';

/** Shared merge for partial GraphQL payloads (Apollo cache warnings). */
function mergePartialObjects<T>(
  existing: T | undefined,
  incoming: T | undefined,
  mergeObjects: (a: T, b: T) => T,
): T | undefined {
  if (!incoming) return existing;
  if (!existing) return incoming;
  return mergeObjects(existing, incoming);
}

/** Preserve array fields when a partial query omits them. */
function mergePreservingArray<T>(existing: T[] | undefined, incoming: T[] | undefined): T[] | undefined {
  if (incoming === undefined) return existing;
  return incoming;
}

/** Normalize when _id is present; keep embedded when partial payloads omit it. */
function keyFieldsById(object: Readonly<Record<string, unknown>>): string[] | false {
  const id = object._id;
  return typeof id === 'string' && id.length > 0 ? ['_id'] : false;
}

const apolloTypePolicies: NonNullable<ConstructorParameters<typeof InMemoryCache>[0]>['typePolicies'] = {
  PlanSource: {
    // PlanSource is an embedded object (no id) inside PlanPage.
    // Without this, a mutation returning partial source fields
    // (e.g. only { scale }) would REPLACE the entire cached source,
    // wiping out type, file, dpi, etc. and causing a blank canvas.
    merge: true,
  },
  PlanPageShapeConfig: {
    merge: true,
  },
  PlanPageShape: {
    keyFields: keyFieldsById,
    fields: {
      config: {
        merge: true,
      },
    },
  },
  PricingSettings: {
    merge: true,
  },
  OutputColumns: {
    merge: true,
  },
  ProposalDocument: {
    keyFields: keyFieldsById,
    merge: true,
    fields: {
      totals: {
        merge: true,
      },
      meta: {
        merge: true,
      },
      sections: {
        merge: mergePreservingArray,
      },
    },
  },
  ProposalTotals: {
    // Embedded object with no id. Partial payloads (e.g. just `grandTotal`)
    // were replacing the full totals object and triggering cache warnings.
    merge: true,
  },
  Product: {
    // Partial queries often request only { name }; disable normalization.
    keyFields: false,
    merge: true,
  },
  ProductConfiguration: {
    keyFields: false,
    merge: true,
  },
  Project: {
    keyFields: ['_id'],
    merge: true,
    fields: {
      // ProjectView fetches name/bidDate without Takeoffs; keep the list
      // written by the takeoffs query on the normalized Project entity.
      Takeoffs: {
        merge: mergePreservingArray,
      },
    },
  },
  Takeoff: {
    // Root takeoff(id:) queries include _id and merge in cache; nested
    // Material.Takeoff { name } payloads without _id stay embedded.
    keyFields: keyFieldsById,
    merge: true,
    fields: {
      Product: {
        merge: true,
      },
      ProductConfiguration: {
        merge: true,
      },
      SubTakeoffs: {
        merge: mergePreservingArray,
      },
      PlanPageShapes: {
        merge: mergePreservingArray,
      },
      extraCharges: {
        merge: mergePreservingArray,
      },
      estimate: {
        merge: true,
      },
      outputColumnOverrides: {
        merge: true,
      },
    },
  },
  Query: {
    fields: {
      getOrganization: {
        merge(existing: any, incoming: any, { mergeObjects }: { mergeObjects: (a: any, b: any) => any }) {
          return mergePartialObjects(existing, incoming, mergeObjects);
        },
      },
      project: {
        keyArgs: ['id'],
        merge(existing: any, incoming: any, { mergeObjects }: { mergeObjects: (a: any, b: any) => any }) {
          return mergePartialObjects(existing, incoming, mergeObjects);
        },
      },
      takeoff: {
        keyArgs: ['id'],
        merge(existing: any, incoming: any, { mergeObjects }: { mergeObjects: (a: any, b: any) => any }) {
          return mergePartialObjects(existing, incoming, mergeObjects);
        },
      },
      planpageshapes: {
        keyArgs: ['planPage', 'takeoffId', 'updatedWithinSeconds'],
        merge(_existing: unknown, incoming: unknown) {
          return incoming;
        },
      },
    },
  },
};

const compairTokens = (current: string, next: string) => {
  if (!current) {
    return true;
  }
  try {
    const expLimit = new Date().getTime() / 1000 + 120;

    const currentDecoded: { exp: number; permissions: string[] | null } =
      jwtDecode(current);
    const nextDecoded: { exp: number; permissions: string[] | null } =
      jwtDecode(next);
    if (currentDecoded.exp < expLimit && nextDecoded.exp > expLimit) {
      return true;
    }
    if (
      currentDecoded.permissions?.sort().join(',') !==
      nextDecoded.permissions?.sort().join(',')
    ) {
      return true;
    }
  } catch (e) {
    console.error(e);
    // console.log('Error', token1, token2);
  }
  return false;
};
const Apollo: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { getSetting } = useSettings();
  const { viewScope } = useAppContext();
  const auth = useAuth();
  const [token, setToken] = useState('');
  const [tokenReady, setTokenReady] = useState(false);
  const [client, setClient] = useState<ApolloClient>();
  const uri = getSetting('GRAPHQL_ENDPOINT');
  const cloneuri = getSetting('GRAPHQL_ENDPOINT_CLONE');
  
  // Use refs to avoid stale closures in authLink
  const tokenRef = useRef(token);
  const viewScopeRef = useRef(viewScope);
  
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  
  useEffect(() => {
    viewScopeRef.current = viewScope;
  }, [viewScope]);

  useEffect(() => {
    let loaded = true;
    const inv = setInterval(() => {
      auth.getToken().then((t) => {
        if (loaded && compairTokens(token, t)) {
          setToken(t);
        }
      }).catch(() => {});
    }, 1000 * 60);
    return () => {
      loaded = false;
      clearInterval(inv);
    };
  }, [token]);

  // Keep token up to date
  useEffect(() => {
    // Don't try to fetch token while auth is still loading
    if (auth.isLoading) {
      return;
    }

    let loaded = true;
    
    // If user is not logged in, we're ready (no token needed for public routes)
    if (!auth.user) {
      setTokenReady(true);
      return;
    }

    // User is logged in - we MUST get a valid token
    auth.getToken()
      .then((t) => {
        if (loaded && t) {
          setToken(t);
          setTokenReady(true);
        }
      })
      .catch((err) => {
        console.error('[Apollo] Failed to get auth token:', err);
        // User is authenticated but token fetch failed
        // This can happen with login_required or missing refresh token errors
        if (err?.error === 'login_required' || err?.message?.includes('Missing Refresh Token')) {
          // Clear stale auth state and redirect to login
          console.warn('[Apollo] Token fetch failed - redirecting to login');
          auth.logout({ logoutParams: { returnTo: window.location.origin } });
        } else {
          // For other errors, still set tokenReady to avoid blank screen
          // App will work without auth or show appropriate error
          console.warn('[Apollo] Token fetch failed with unexpected error, continuing without token');
          setTokenReady(true);
        }
      });
    return () => { loaded = false; };
  }, [auth.isLoading, auth.user]);

  // authLink uses refs to always get the latest token/viewScope values
  // This avoids stale closure issues when the token updates
  const authLink = useMemo(
    () =>
      setContext((_, { headers }) => {
        const currentToken = tokenRef.current;
        const currentViewScope = viewScopeRef.current;
        // return the headers to the context so httpLink can read them
        return {
          headers: {
            ...headers,
            authorization: currentToken ? `Bearer ${currentToken}` : '',
            'x-view-scope': currentViewScope,
          },
        };
      }),
    [], // No deps - uses refs internally
  );
  
  useEffect(() => {
    // Only create client once we're ready (have token for logged in users, or no user)
    if (!tokenReady) {
      return;
    }

    // Error link to log and handle network errors
    const errorLink = onError(({ error, operation }) => {
      if (CombinedGraphQLErrors.is(error)) {
        const opName = operation.operationName || '(unnamed)';
        error.errors.forEach(({ message, locations, path }) =>
          console.error(
            `[GraphQL error] ${opName}: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`,
          ),
        );
        return;
      }
      const networkError = error instanceof Error ? error : null;
      if (networkError) {
        console.error(`[Network error]: ${networkError.message}`, {
          operation: operation.operationName,
          statusCode: 'statusCode' in networkError ? (networkError as { statusCode?: number }).statusCode : 'unknown',
          response:
            'result' in networkError ? (networkError as { result?: unknown }).result : null,
        });
        if (
          'statusCode' in networkError &&
          (networkError as { statusCode?: number }).statusCode === 404
        ) {
          console.error('[Apollo] 404 error - endpoint may not exist or backend issue', {
            uri,
            operation: operation.operationName,
          });
        }
      }
    });

    const httpLink = createHttpLink({
      uri,
    });
    const clonerHttpLink = createHttpLink({
      uri: cloneuri,
    });
    const cache = new InMemoryCache({
      typePolicies: apolloTypePolicies,
    });
    setClient(
      new ApolloClient({
        link: ApolloLink.from([
          errorLink,
          ApolloLink.split(
            operation => operation.getContext().clientName === 'cloner',
            authLink.concat(clonerHttpLink),
            authLink.concat(httpLink)
          )
        ]),
        cache,
      }),
    );
  }, [tokenReady, uri, authLink, cloneuri]);
  
  // Don't render children until we have a token and client
  if (!tokenReady || !client) {
    return <></>;
  }
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
export { useApolloClient, useLazyQuery, useMutation, useQuery, Apollo, gql };
